import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import type { DemoData } from '../domain/types';
import { initialUi, reducer, type AppState } from './reducer';
import { createActions, type Actions } from './actions';
import { createSeed, SEED_VERSION } from '../data/seed';
import { isDemo } from '../lib/clock';
import { api, apiActive } from '../services/api';
import { sessionFromBootstrap } from '../services/auth';
import { ECOSYSTEM_SERIES } from '../domain/series';

const STORAGE_KEY = 'ugain.demo.v1';

/** Server data state. Only 'ready' means the screens show data from the UGAIN API. */
export type ServerStatus = 'demo' | 'idle' | 'loading' | 'ready' | 'error';

// Demo mode keeps local changes on this device so reviewers can follow a
// journey across reloads. Api mode never persists server data locally and
// never falls back to the seed: screens render only after GET /me/bootstrap.
function loadData(): DemoData {
  if (!isDemo) return emptyData();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { version?: number; data?: DemoData };
      if (parsed.version === SEED_VERSION && parsed.data) return parsed.data;
    }
  } catch {
    /* corrupted or unavailable storage: fall back to seed */
  }
  return createSeed();
}

/** Placeholder before the first bootstrap (never rendered by screens; see DataGate). */
export function emptyData(): DemoData {
  return {
    me: '', ambassadors: {}, activities: {}, programmes: [], tasks: [], updates: [], pulse: [], offers: [], chats: [],
    helpPosted: {}, followedActivities: {}, followedPeople: {}, offeredHelp: {}, shareKits: {}, shareRecords: [],
    privacy: { publicProfile: false, showCity: true, showSocials: false }, series: [...ECOSYSTEM_SERIES],
  };
}

interface Ctx {
  state: AppState;
  act: Actions;
  /** True only when the data on screen came from the UGAIN API. */
  live: boolean;
  server: ServerStatus;
  /** Api mode: (re)load GET /me/bootstrap. Resolves false on failure. */
  reload: () => Promise<boolean>;
  /** Api mode: drop server data (sign-out). */
  clear: () => void;
}
const AppContext = createContext<Ctx | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, (): AppState => ({ data: loadData(), ui: initialUi }));
  const [server, setServer] = useState<ServerStatus>(isDemo ? 'demo' : 'idle');
  const ref = useRef(state);
  useEffect(() => {
    ref.current = state;
  });

  const loads = useRef(0);
  const reload = useCallback(async (): Promise<boolean> => {
    if (!apiActive) return false;
    const n = ++loads.current;
    setServer((s) => (s === 'ready' ? s : 'loading'));
    try {
      const data = await api.bootstrap();
      if (n !== loads.current) return true; // a newer load superseded this one
      sessionFromBootstrap(data);
      dispatch({ type: 'demo/reset', data });
      setServer('ready');
      return true;
    } catch {
      if (n === loads.current) setServer((s) => (s === 'ready' ? s : 'error'));
      return false;
    }
  }, []);
  const clear = useCallback(() => {
    loads.current++;
    dispatch({ type: 'demo/reset', data: emptyData() });
    setServer(isDemo ? 'demo' : 'idle');
  }, []);

  const act = useMemo(() => createActions(dispatch, () => ref.current, reload), [reload]);

  useEffect(() => {
    if (!isDemo) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: SEED_VERSION, data: state.data }));
    } catch {
      /* storage full or blocked */
    }
  }, [state.data]);

  const toast = state.ui.toast;
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => dispatch({ type: 'ui/clearToast', id: toast.id }), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const value = useMemo(() => ({ state, act, live: server === 'ready', server, reload, clear }), [state, act, server, reload, clear]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): Ctx {
  const c = useContext(AppContext);
  if (!c) throw new Error('useApp must be used inside <AppProvider>');
  return c;
}
